import {defineStore} from "pinia/dist/pinia";
import AppService from "@/service/AppService";
import AccreditationService from "@/service/AccreditationService";
import {useApplicationStore} from "@/store/applicationStore";

export const useAccreditationStore = defineStore('AccreditationStore', {
    state() {
        return {
            appService: new AppService(),
            accreditationService: new AccreditationService(),
            showPersonalState: true,
            showWorkState: false,
            showTermsAndConditionsState: false,
            showSubmittedState: false,
            personalFieldsState: [],
            workFieldsState: [],
            attendanceFieldsState: [],
            termsConditionsFieldsState: [],
            countriesState: [],
            salutationsState: [],
            gendersState: [],
            professionsState: [],
            primaryActivitiesState: [],
            accountsState: [],
            rolesState: [],
            mediumsState: [],
            publicationTypesState: [],
            languagesState: [],
            publicationTargetsState: [],
            editorialInterestsState: [],
            focusAreasState: [],
            howToContactTypesState: [],
            attendanceTypesState: [],
            filteredAccountsState: [],
            selectedPhoneCodeState: null,
            accreditedMeetingsState: [],
            selectedMeetingState: null,
            filmingActivitiesState: [],
        }
    },
    getters: {
        showPersonal() {
            return this.showPersonalState
        },
        selectedPhoneCode() {
            return this.selectedPhoneCodeState
        },
        showWork() {
            return this.showWorkState
        },
        showTermsAndConditions() {
            return this.showTermsAndConditionsState
        },
        showSubmitted() {
            return this.showSubmittedState
        },
        personalFields() {
            return this.personalFieldsState
        },
        workFields() {
            return this.workFieldsState
        },
        attendanceFields() {
            return this.attendanceFieldsState
        },
        termsConditionsFields() {
            return this.termsConditionsFieldsState
        },
        countries() {
            return this.countriesState
        },
        salutations() {
            return this.salutationsState
        },
        genders() {
            return this.gendersState
        },
        professions() {
            return this.professionsState
        },
        primaryActivities() {
            return this.primaryActivitiesState
        },
        roles() {
            return this.rolesState
        },
        mediums() {
            return this.mediumsState
        },
        publicationTypes() {
            return this.publicationTypesState
        },
        languages() {
            return this.languagesState
        },
        publicationTargets() {
            return this.publicationTargetsState
        },
        editorialInterests() {
            return this.editorialInterestsState
        },
        focusAreas() {
            return this.focusAreasState
        },
        howToContactTypes() {
            return this.howToContactTypesState
        },
        attendanceTypes() {
            const applicationStore = useApplicationStore()
            if (applicationStore.meeting != null && applicationStore.meeting.format === 'Live') {
                return this.attendanceTypesState.filter(attendaceType => attendaceType.id !== 'Remotely')
            }
            return this.attendanceTypesState
        },
        filteredAccounts() {
            return this.filteredAccountsState
        },
        accreditedMeetings() {
            return this.accreditedMeetingsState
        },
        selectedMeeting() {
            return this.selectedMeetingState
        },
        filmingActivities() {
            return this.filmingActivitiesState
        },        
    },
    actions: {
        setShowPersonal(value) {
            this.showPersonalState = value
        },
        setSelectedPhoneCode(value) {
            this.selectedPhoneCodeState = value
        },
        setShowWork(value) {
            this.showWorkState = value
        },
        setShowTermsAndConditions(value) {
            this.showTermsAndConditionsState = value
        },
        setShowSubmitted(value) {
            this.showSubmittedState = value
        },
        setPersonalFields(fields) {
            this.personalFieldsState = fields
        },
        setWorkFields(fields) {
            this.workFieldsState = fields
        },
        setAttedanceFields(fields) {
            this.attendanceFieldsState = fields
        },
        setTermsConditionsFields(fields) {
            this.termsConditionsFieldsState = fields
        },
        setCountries(countries) {
            this.countriesState = countries
        },
        setSalutations(salutations) {
            this.salutationsState = salutations
        },
        setGenders(genders) {
            this.gendersState = genders
        },
        setProfessions(professions) {
            this.professionsState = professions
        },
        setPrimaryActivities(primaryActivities) {
            this.primaryActivitiesState = primaryActivities
        },
        setRoles(roles) {
            this.rolesState = roles
        },
        setMediums(mediums) {
            this.mediumsState = mediums
        },
        setPublicationTypes(publicationTypes) {
            this.publicationTypesState = publicationTypes
        },
        setLanguages(languages) {
            this.languagesState = languages
        },
        setPublicationTargets(publicationTargets) {
            this.publicationTargetsState = publicationTargets
        },
        setEditorialInterests(editorialInterests) {
            this.editorialInterestsState = editorialInterests
        },
        setFocusAreas(focusAreas) {
            this.focusAreasState = focusAreas
        },
        setAttendanceTypes(attendanceTypes) {
            this.attendanceTypesState = attendanceTypes
        },
        setFilmingActivities(filmingActivities) {
            this.filmingActivitiesState = filmingActivities
        },
        setHowToContactTypes(howToContactTypes) {
            this.howToContactTypesState = howToContactTypes
        },
        setFilteredAccounts(filteredAccounts) {
            this.filteredAccountsState = filteredAccounts
        },
        setAccreditedMeetings(accreditedMeetings) {
            this.accreditedMeetingsState = accreditedMeetings
        },
        setSelectedMeeting(selectedMeeting) {
            this.selectedMeetingState = selectedMeeting
        },
        initSalutations() {
            this.appService.salutations().then(salutations => this.setSalutations(salutations.map(salutation => {
                return {id: salutation, label: salutation}
            })))
        },
        initGenders() {
            this.appService.genders().then(genders => this.setGenders(genders.map(gender => {
                return {id: gender, label: gender}
            })))
        },
        initCountries() {
            this.appService.countries().then(countries => this.setCountries(countries.map(country => {
                return {id: country.id, label: country.name, isoCode: country.isoCode}
            })))
        },
        initProfessions() {
            this.appService.professions().then(professions => this.setProfessions(professions.map(profession => {
                return {id: profession, label: profession}
            })))
        },
        initPrimaryActivities() {
            this.appService.primaryActivities().then(primaryActivities => this.setPrimaryActivities(primaryActivities.map(primaryActivity => {
                return {id: primaryActivity, label: primaryActivity}
            })))
        },
        initRoles() {
            this.appService.roles().then(roles => this.setRoles(roles.map(role => {
                return {id: role, label: role}
            })))
        },
        initMediums() {
            this.appService.mediums().then(mediums => this.setMediums(mediums.map(medium => {
                return {id: medium, label: medium}
            })))
        },
        initPublicationLanguages() {
            this.appService.publicationLanguages().then(publicationLanguages => this.setLanguages(publicationLanguages.map(publicationLanguage => {
                return {id: publicationLanguage, label: publicationLanguage}
            })))
        },
        initPublicationTargets() {
            this.appService.publicationTargets().then(publicationTargets => this.setPublicationTargets(publicationTargets.map(publicationTarget => {
                return {id: publicationTarget, label: publicationTarget}
            })))
        },
        initPublicationTypes() {
            this.appService.publicationTypes().then(publicationTypes => this.setPublicationTypes(publicationTypes.map(publicationType => {
                return {id: publicationType, label: publicationType}
            })))
        },
        initEditorialInterests() {
            this.appService.editorialInterests().then(editorialInterests => this.setEditorialInterests(editorialInterests.map(editorialInterest => {
                return {key: editorialInterest?.id, label: editorialInterest?.name, data:editorialInterest?.name, children: editorialInterest?.children}
            })))
        },
        initFocusAreas() {
            this.appService.focusAreas().then(focusAreas => this.setFocusAreas(focusAreas.map(focusArea => {
                return {key: focusArea?.id, label: focusArea?.name, data:focusArea?.name, children: focusArea?.children}
            })))
        },
        initAttendanceTypes() {
            this.appService.attendanceTypes().then(attendanceTypes => this.setAttendanceTypes(attendanceTypes.map(attendanceType => {
                return {id: attendanceType, label: attendanceType}
            })))
        },
        initFilmingActivities() {
            const activities = [
                { id: 'Yes', label: 'Yes' },
                { id: 'No', label: 'No' }
            ];
            this.setFilmingActivities(activities);
        },
        initHowToContactTypes() {
            this.appService.howToContactTypes().then(howToContactTypes => this.setHowToContactTypes(howToContactTypes.map(howToContactType => {
                return {id: howToContactType, label: howToContactType}
            })))
        },
        initAccreditedMeetings() {
            this.accreditationService.accreditedMeetings().then(accreditedMeetings => this.setAccreditedMeetings(accreditedMeetings))
        },
        meetingProgramme(meetingId) {
            return this.accreditationService.meetingProgramme(meetingId)
        },
        pressKit(meetingCode) {
            return this.accreditationService.pressKit(meetingCode)
        },
        filterAccountsByCountry(country) {
            this.appService.accountsByCountry(country).then(accounts => {
                this.setFilteredAccounts(accounts.map(account => {
                    return {id: account.id, label: account.name, country: account.country.id, website: account.website, publisher: account.publisher}
                }))
            })
        },
        setPhoneCodeByCountry(countryID) {
            const applicationStore = useApplicationStore()
            const country = this.countries.find(c => String(c.id).toUpperCase() === String(countryID).toUpperCase())
            if (country != null) {
                this.setSelectedPhoneCode(applicationStore.countryPhoneCodes.find(element => String(element.iso).toUpperCase() === String(country.isoCode).toUpperCase()))
            }
        },
        upsertRegistration(registration) {
            return this.accreditationService.upsertRegistration(registration);
        },
        insertAccreditation(accreditation) {
            return this.accreditationService.insertAccreditation(accreditation);
        },
        publication(accountId, contactId) {
            return this.accreditationService.publication(accountId, contactId);
        },
        logout() {
            this.$reset()
        }
    },
    persist: {
        storage: sessionStorage
    }
})
